/**
 * The device schema as Drizzle tables: the TypeScript source of truth for
 * what the local SQLite database holds.
 *
 * This file defines; it does not create. The DDL that actually runs on the
 * device is the hand-written migration ladder in db-migrations.ts —
 * drizzle-kit is deliberately not in the loop, so no bundled .sql files and no
 * metro changes. THE TWO MUST CHANGE TOGETHER: a column that moves here and
 * not there is a query that compiles and then fails on a real phone.
 *
 * **Which tables carry a `tenant_id`, and which do not.** Only the three that
 * are scanned WITHOUT naming a record: `entry_mirror`, `outbox` and
 * `record_catalog`. The rest are keyed by record id, and a record id is a uuid
 * — unique across businesses — so a keyed lookup cannot bleed from one
 * business into another. A business-wide scan over one of them goes through
 * the catalog instead: `record_id IN (SELECT id FROM record_catalog WHERE
 * tenant_id = ?)`. Added by migration 6.
 *
 * Five tables:
 *
 *   entry_mirror    every entry version the device knows, pending or synced
 *   outbox          writes not yet accepted by the server
 *   record_catalog  the home screen's records, as the last pull sent them
 *   schema_cache    one RecordSchemaResponse per record, keyed by field set
 *   sync_meta       cursors: 'since:<tenantId>' -> server_seq as text, and the
 *                   other small facts about this device: 'me', 'active_tenant'
 *   insight_layout  which insight cards sit on a record, in what order
 *   search_doc      one row per indexed entry, beside the FTS5 search table
 *
 * And one table Drizzle CANNOT model: `entry_search`, an FTS5 virtual table.
 * It is created by the migration in db.ts and read only through raw SQL, for
 * the same reason `v_<record>` is: the query builder cannot express MATCH or
 * bm25, and nothing table-shaped reads it.
 */
import { sql } from 'drizzle-orm';
import { index, integer, primaryKey, real, sqliteTable, text } from 'drizzle-orm/sqlite-core';

export const entryMirror = sqliteTable(
  'entry_mirror',
  {
    entryId: text('entry_id').primaryKey(),
    recordId: text('record_id').notNull(),
    /**
     * Which business this row belongs to.
     *
     * Nullable because SQLite's ALTER TABLE allows nothing else, and that is
     * also the behaviour wanted: a NULL matches no tenant predicate, so a row
     * nothing labelled is hidden from everybody rather than shown to everybody.
     * Every write path sets it. Added by migration 6.
     */
    tenantId: text('tenant_id'),
    /** 0 until the server acks. */
    version: integer('version').notNull(),
    versionId: text('version_id'),
    /** Null while pending. */
    serverSeq: integer('server_seq'),
    isCurrent: integer('is_current', { mode: 'boolean' }).notNull().default(true),
    deletedAt: text('deleted_at'),
    /** Materialized from the time_axis binding, via dayOf. */
    day: text('day'),
    /** Materialized measure × sign, via measureOf/signOf. */
    signedValue: real('signed_value'),
    pending: integer('pending', { mode: 'boolean' }).notNull().default(false),
    /** Field-id keyed EntryValues. */
    valuesJson: text('values_json').notNull(),
    createdBy: text('created_by'),
    sourceChannel: text('source_channel'),
    /**
     * Which Link wrote this row, and the entry it was written from. Both null
     * on a row a person typed. The pair draws the Auto badge and refuses a
     * direct edit, so it must survive offline as well as it does on the server.
     * Added by migration 4.
     */
    linkId: text('link_id'),
    sourceEntryId: text('source_entry_id'),
    createdAt: text('created_at').notNull(),
    updatedAt: text('updated_at').notNull(),
  },
  (table) => [
    index('entry_mirror_list')
      .on(table.recordId, sql`${table.day} DESC`)
      .where(sql`${table.deletedAt} IS NULL`),
    index('entry_mirror_seq').on(table.serverSeq),
    index('entry_mirror_agg')
      .on(table.recordId, table.day)
      .where(sql`${table.deletedAt} IS NULL`),
    /**
     * The business's own day index. Added by migration 6.
     *
     * A business-wide card asks "this business, these days", and a day-only
     * index makes SQLite read every OTHER business's rows for that day and
     * throw them away. Leading on the tenant turns that into one seek.
     *
     * It replaced `entry_mirror_day`, which migration 3 added for the same
     * cards and migration 7 dropped: with `BusinessContext.tenantId` required,
     * no statement can be compiled that a day-only index could serve, and an
     * index no query can use is a second b-tree written on every entry.
     */
    index('entry_mirror_tenant_day')
      .on(table.tenantId, table.day)
      .where(sql`${table.deletedAt} IS NULL`),
  ],
);

export const outbox = sqliteTable(
  'outbox',
  {
    opId: text('op_id').primaryKey(),
    /**
     * 'create' or 'edit'. A create carries a row the server has never seen; an
     * edit carries only the keys a person changed on a row it already holds.
     * Both are text in the same column, so the kind the sheet's re-marking
     * added needed no migration of the device database.
     */
    kind: text('kind').notNull(),
    recordId: text('record_id').notNull(),
    entryId: text('entry_id').notNull(),
    /**
     * Which business's write this is. Added by migration 6, and the reason the
     * column exists at all: `push` sends every op it is handed under the tenant
     * id it was called with, so an op of another business is REFUSED as a 4xx,
     * read as final, and dropped. That is somebody's unsent sale, gone.
     */
    tenantId: text('tenant_id'),
    valuesJson: text('values_json').notNull(),
    /**
     * The lines the write carries, keyed by `lines` field id, at both levels,
     * as `EntryLinesWrite`. Null when it names none. Added by migration 14.
     */
    linesJson: text('lines_json'),
    /**
     * Each line row an edit wrote over, as it stood before the edit, so a
     * refusal can put it back (`dropEditOp`). Null on a create and on an op
     * that wrote no lines. Added by migration 15.
     */
    linesBeforeJson: text('lines_before_json'),
    fieldSetVer: integer('field_set_ver'),
    /**
     * NOTHING WRITES THIS, and the column stays for one reason: dropping it
     * needs a migration, and an unused column costs a phone nothing.
     *
     * It was written once, by a `failOp` that `removePending` then deleted the
     * row of on the very next line — so the reason a write was refused was
     * stored and destroyed in the same transaction, and the person was told
     * nothing. `refusedWrites` in `stores/sync-store.ts` holds it now, in
     * memory, where a refusal outlives the healthy syncs that follow it.
     * Do not revive this: an outbox row kept for its reason would be counted
     * by `outboxCount` as a write still waiting to go, forever.
     */
    failedReason: text('failed_reason'),
    createdAt: text('created_at').notNull(),
  },
  /** Drain order within one business: the push reads it tenant, then oldest. */
  (table) => [index('outbox_tenant').on(table.tenantId, table.createdAt)],
);

export const recordCatalog = sqliteTable(
  'record_catalog',
  {
    id: text('id').primaryKey(),
    /**
     * Whose record this is. Added by migration 6.
     *
     * The catalog is the table every other tenant-wide scan is scoped THROUGH,
     * so this column is the root of the whole scheme — and `pruneCatalog` is
     * why it had to exist: a pull for one business, seeing another's rows here,
     * would delete them as no longer readable.
     */
    tenantId: text('tenant_id'),
    name: text('name').notNull(),
    kind: text('kind', { enum: ['record', 'directory'] }).notNull(),
    nounKey: text('noun_key'),
    entryNounKey: text('entry_noun_key'),
    currency: text('currency'),
    templateId: text('template_id'),
    direction: text('direction', { enum: ['in', 'out', 'mixed'] }),
    /**
     * What this record holds, as the glyph draws it. Migration 10.
     *
     * The EFFECTIVE name the server answered, not the stored one: a directory
     * arrives carrying `people` or `list`, a record carrying whatever it was
     * given or nothing. So home reads a column and works nothing out.
     *
     * Plain text and not an enum, on purpose. A device holds rows a NEWER
     * server wrote, and a word this build does not know must not be typed as
     * one it does: `listCatalog` puts it through `isRecordIconId` and draws the
     * default rather than an empty square. Null is a device that has not synced
     * since this shipped, and reads the same as "no icon".
     */
    icon: text('icon'),
    entryCount: integer('entry_count').notNull().default(0),
    headlineValue: real('headline_value'),
    /**
     * The oldest day this record still holds, or null when it holds everything.
     * Added by migration 12.
     *
     * Written by the prune and read by the card compiler. A card whose window
     * starts before this day cannot be answered from the rows, because the rows
     * are not here; it is answered from `aggregate_bucket`, or it draws the
     * quiet placeholder.
     *
     * PER RECORD, not per business. The cap is a row count, and a record that
     * takes four hundred rows a day reaches it in a week while a quiet one
     * never reaches it at all. One floor for both would either strand the quiet
     * record's history or claim the busy record still had history it does not.
     */
    floorDay: text('floor_day'),
    lastEntryAt: text('last_entry_at'),
    fieldSetVer: integer('field_set_ver').notNull().default(0),
    /**
     * What THIS device's person may do in this record, as the last pull sent it.
     * Added by migration 8, and every column is nullable on purpose.
     *
     * Null means one of two things and the reader treats both the same way:
     * an OWNER, who holds everything without a grant row, or a device that has
     * not synced since migration 8 shipped. `record-grant.ts` reads null as
     * PERMISSIVE for that reason — hiding a button on missing data would lock a
     * person out of their own records after an upgrade, offline.
     *
     * A courtesy and never a fence. The server refuses regardless, and it is
     * the only thing that does.
     */
    grantCapabilities: text('grant_capabilities'),
    grantRowScope: text('grant_row_scope', { enum: ['all', 'own_entries'] }),
    grantWriteMode: text('grant_write_mode', { enum: ['direct', 'submit_draft', 'request'] }),
    grantBalanceVisible: integer('grant_balance_visible', { mode: 'boolean' }),
    grantDirection: text('grant_direction', { enum: ['in_only', 'out_only'] }),
    /**
     * The field ids this person may write, as a JSON array. Migration 9. Null
     * means every field — an owner, a grant that does not narrow, or a device
     * that has not synced since it shipped — per the same rule as the five
     * above.
     */
    grantWritableFields: text('grant_writable_fields'),
    /**
     * Migration 13 (web-redesign-04 phase 3). `grantReach` is the summary's
     * `reach` as JSON, `grantTeam` the authors Their team covers as a JSON
     * array, `grantMask` the digest of the fields hidden here. Null is
     * unknown: an owner, an older server, or a device not yet synced since.
     */
    grantReach: text('grant_reach'),
    grantTeam: text('grant_team'),
    grantMask: text('grant_mask'),
  },
  (table) => [index('record_catalog_tenant').on(table.tenantId)],
);

export const schemaCache = sqliteTable('schema_cache', {
  recordId: text('record_id').primaryKey(),
  fieldSetVer: integer('field_set_ver').notNull(),
  /** RecordSchemaResponse, serialized. */
  schemaJson: text('schema_json').notNull(),
});

export const syncMeta = sqliteTable('sync_meta', {
  key: text('key').primaryKey(),
  value: text('value').notNull(),
});

/**
 * Which insight cards sit on one record's entry list, and in what order.
 *
 * The whole ordered list lives in one JSON column, as schema_cache holds a
 * whole schema: add, remove and reorder are then one atomic write, and the
 * order is simply the array order.
 *
 * NO ROW means this record has never been customized, so the layout is
 * derived from its summary view. A row holding `[]` means the user removed
 * every card. Absence and emptiness are different answers, on purpose.
 */
export const insightLayout = sqliteTable('insight_layout', {
  recordId: text('record_id').primaryKey(),
  /** InsightLayoutEntry[], serialized. */
  layoutJson: text('layout_json').notNull(),
  updatedAt: text('updated_at').notNull(),
});

/**
 * Where the whole business's insight cards sit, and in what order.
 *
 * The same shape as `insightLayout`, keyed by tenant instead of by record, and
 * a table of its own rather than a reserved row in that one — `pruneCatalog`
 * deletes from `insight_layout` by record id, and a business row hiding there
 * would need a carve-out in every maintenance path written after it.
 *
 * Absence and emptiness differ here too: no row means the business has never
 * been customized, so the layout is derived from what its records are; a row
 * holding `[]` means somebody took every card off.
 */
export const businessInsightLayout = sqliteTable('business_insight_layout', {
  tenantId: text('tenant_id').primaryKey(),
  /** BusinessInsightLayoutEntry[], serialized. */
  layoutJson: text('layout_json').notNull(),
  updatedAt: text('updated_at').notNull(),
});

/**
 * One row per indexed entry, beside the FTS5 table that holds its words.
 *
 * `entry_search` is content-full FTS5 and cannot be looked up by anything but
 * its own rowid: an UNINDEXED column would make "delete the document for this
 * entry" a scan of the whole index. So the addressable columns live here, and
 * `doc_id` IS `entry_search.rowid`.
 *
 * The three columns beside it are all things the index must NOT hold:
 *
 *   entry_id   what a write path has in hand when it must replace a document
 *   record_id  per-record rebuild, per-record prune, and record-scoped search
 *   day        the recency half of the ranking, sorted OUTSIDE the index
 *
 * The document itself is NOT here. It lives in `entry_search`, because that is
 * the table that tokenizes it.
 */
export const searchDoc = sqliteTable(
  'search_doc',
  {
    /** = entry_search.rowid. Assigned by SQLite, never by the app. */
    docId: integer('doc_id').primaryKey(),
    entryId: text('entry_id').notNull().unique(),
    recordId: text('record_id').notNull(),
    /** The entry's day, or null when its record has no time axis. */
    day: text('day'),
  },
  (table) => [index('search_doc_record').on(table.recordId)],
);

/**
 * One day of one record's measure, pre-summed by the server. Migration 12.
 *
 * The mirror of `data.aggregate` scope `record_day`, and the reason a device
 * may throw rows away at all: a card that reaches past the floor reads these
 * instead. It is small by construction — one row per record, per measure, per
 * day, so about eleven hundred a year for a record however many entries it
 * takes — and it is never pruned.
 *
 * Four numbers on one row: the net, the two sides of it, and the count. A net
 * cannot be taken apart again once it is summed, so "money in" is counted as it
 * is written rather than worked out later.
 *
 * It holds only what the server has accepted. A pending row is in the mirror
 * and in no bucket, which is why every read of this table adds the pending rows
 * of the same window back on.
 */
export const aggregateBucket = sqliteTable(
  'aggregate_bucket',
  {
    tenantId: text('tenant_id').notNull(),
    recordId: text('record_id').notNull(),
    /** `yyyy-mm-dd`. */
    day: text('day').notNull(),
    /** The record's measure field, or null when it binds none and only the count is here. */
    measureFieldId: text('measure_field_id'),
    /** The measure, summed and signed. Nought when this caller may not see it. */
    value: real('value').notNull().default(0),
    rows: integer('rows').notNull().default(0),
    moneyIn: real('money_in').notNull().default(0),
    moneyOut: real('money_out').notNull().default(0),
    asOfSeq: integer('as_of_seq').notNull(),
  },
  (table) => [
    primaryKey({ columns: [table.tenantId, table.recordId, table.day] }),
    index('aggregate_bucket_span').on(table.recordId, table.day),
    index('aggregate_bucket_tenant').on(table.tenantId, table.day),
  ],
);
